import { Coins, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/features/auth';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';

import {
  useCreateCurrency,
  useCurrencies,
  useSetCurrencyStatus,
  useUpdateCurrency,
} from '../api/pricing.queries';
import type { Currency } from '../types/pricing.types';

const REASON_CLASS =
  'border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent p-2 text-sm outline-none focus-visible:ring-[3px]';

function usage(currency: Currency) {
  const parts: string[] = [];
  if (currency.countries.length > 0) parts.push(currency.countries.join(', '));
  if (currency.providers.length > 0) parts.push(`providers: ${currency.providers.join(', ')}`);
  return parts.join(' · ');
}

/**
 * Currencies (BR §7.4) and what uses each. Changing them needs
 * countries.manage; the backend refuses to deactivate a currency an active
 * country prices in, and asks for the usage to be acknowledged otherwise.
 */
export function CurrenciesPage() {
  const currencies = useCurrencies();
  const { can } = usePermissions();
  const canManage = can('countries.manage');
  const [editing, setEditing] = useState<Currency | 'new' | null>(null);
  const [toggling, setToggling] = useState<Currency | null>(null);

  return (
    <PageContainer>
      <PageHeader
        title="Currencies"
        description="Currencies countries price and settle in, and providers charge in."
        actions={
          canManage ? (
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden />
              Add currency
            </Button>
          ) : undefined
        }
      />

      {currencies.isLoading ? (
        <Skeleton className="h-48" />
      ) : currencies.error ? (
        <ErrorState error={currencies.error} onRetry={() => void currencies.refetch()} />
      ) : (currencies.data ?? []).length === 0 ? (
        <EmptyState icon={Coins} title="No currencies yet" />
      ) : (
        <div className="bg-card overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead>Used by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(currencies.data ?? []).map((currency) => (
                <TableRow key={currency.code}>
                  <TableCell>
                    <span className="font-mono text-xs font-semibold">{currency.code}</span>{' '}
                    {currency.name}
                  </TableCell>
                  <TableCell>{currency.symbol ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {currency.inUse ? usage(currency) : 'Not in use'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={currency.isActive ? 'success' : 'neutral'}>
                      {currency.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-end">
                    <span className="flex justify-end gap-1.5">
                      <HistoryButton
                        entityType="Currency"
                        entityId={currency.code}
                        name={currency.code}
                      />
                      {canManage && (
                        <span className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            aria-label={`Edit ${currency.code}`}
                            onClick={() => setEditing(currency)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7"
                            aria-label={`${currency.isActive ? 'Deactivate' : 'Activate'} ${currency.code}`}
                            onClick={() => setToggling(currency)}
                          >
                            {currency.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        </span>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editing && (
        <CurrencyDialog
          currency={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {toggling && <CurrencyStatusDialog currency={toggling} onClose={() => setToggling(null)} />}
    </PageContainer>
  );
}

function CurrencyDialog({ currency, onClose }: { currency: Currency | null; onClose: () => void }) {
  const create = useCreateCurrency();
  const update = useUpdateCurrency();
  const [code, setCode] = useState('');
  const [name, setName] = useState(currency?.name ?? '');
  const [symbol, setSymbol] = useState(currency?.symbol ?? '');
  const [reason, setReason] = useState('');
  const mutation = currency ? update : create;
  const error = mutation.error instanceof ApiError ? mutation.error.message : null;

  const valid =
    (currency !== null || /^[A-Za-z]{3}$/.test(code.trim())) &&
    name.trim().length > 0 &&
    reason.trim().length >= 3;

  const submit = () => {
    const payload = {
      name: name.trim(),
      symbol: symbol.trim() === '' ? null : symbol.trim(),
      reason: reason.trim(),
    };
    if (currency) update.mutate({ code: currency.code, ...payload }, { onSuccess: onClose });
    else create.mutate({ code: code.trim().toUpperCase(), ...payload }, { onSuccess: onClose });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            {currency ? `Edit ${currency.code}` : 'Add currency'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {currency
              ? 'The code never changes.'
              : 'Use the three-letter ISO code. New currencies start active.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) submit();
          }}
        >
          {!currency && (
            <div className="space-y-1.5">
              <Label htmlFor="currency-code">Code</Label>
              <Input
                id="currency-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                maxLength={3}
                className="font-mono uppercase"
                autoFocus
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="currency-name">Name</Label>
            <Input
              id="currency-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={50}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="currency-symbol">Symbol</Label>
            <Input
              id="currency-symbol"
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
              maxLength={10}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="currency-reason">Reason</Label>
            <textarea
              id="currency-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              maxLength={500}
              className={REASON_CLASS}
            />
          </div>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || mutation.isPending}>
              {mutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {currency ? 'Save' : 'Add currency'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CurrencyStatusDialog({ currency, onClose }: { currency: Currency; onClose: () => void }) {
  const setStatus = useSetCurrencyStatus();
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const deactivating = currency.isActive;
  const needsAcknowledgement = deactivating && currency.inUse;

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={deactivating ? `Deactivate ${currency.code}?` : `Activate ${currency.code}?`}
      description={
        needsAcknowledgement
          ? `${currency.code} is still used by ${usage(currency)}.`
          : deactivating
            ? `${currency.code} is not used anywhere.`
            : `${currency.code} becomes available to countries and providers.`
      }
      confirmLabel={deactivating ? 'Deactivate' : 'Activate'}
      variant={deactivating ? 'destructive' : 'default'}
      isPending={setStatus.isPending}
      confirmDisabled={reason.trim().length < 3 || (needsAcknowledgement && !acknowledged)}
      onConfirm={() =>
        setStatus.mutate(
          {
            code: currency.code,
            isActive: !deactivating,
            reason: reason.trim(),
            acknowledgeUsage: acknowledged,
          },
          { onSuccess: onClose },
        )
      }
    >
      <div className="space-y-3">
        {needsAcknowledgement && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="currency-acknowledge"
              checked={acknowledged}
              onCheckedChange={(checked) => setAcknowledged(checked === true)}
            />
            <Label htmlFor="currency-acknowledge" className="text-sm font-normal">
              I understand what still uses {currency.code}
            </Label>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="currency-status-reason" className="text-xs">
            Reason
          </Label>
          <textarea
            id="currency-status-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            maxLength={500}
            className={REASON_CLASS}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}
